/* eslint-disable no-underscore-dangle, import/no-unresolved, import/extensions */

import React from 'react';

import isNumber from 'lodash/isNumber';

import { type I18nContextType } from './contexts/I18nProvider';

import amountCheck from './amountCheck';
import state from './state';

export interface I18n {
  lang: string;
  locale: string;
  country: string;
  messages: Record<string, string>;
}

export const DEFAULT_LANGUAGE = 'en';
export const SUPPORTED_LANGUAGES = ['en', 'fr'];

const currentScriptSrc = globalThis.document?.currentScript?.src;

let moment = null;
let lang = null;

export function setLang(newLang) {
  lang = newLang;
  state.lang = lang;
  if (moment) {
    moment.locale(lang);
  }
}

export function setTranslations(language, translation) {
  state.translations[language] = translation;
  setLang(language);
}

export function getActiveLang() {
  if (!lang) {
    setLang(state.lang || null);
  }

  if (SUPPORTED_LANGUAGES.indexOf(lang) === -1) {
    setLang(DEFAULT_LANGUAGE);
  }

  return lang;
}

export function getActiveLocale() {
  return getActiveLang().toLowerCase().replace('_', '-');
}

export function getActiveCountry() {
  return state.country;
}

export function setActiveCountry(countryCode) {
  state.country = countryCode;
}

export function getAllTranslations(): Record<string, Record<string, string>> {
  return state.translations;
}

function getTranslatedStrings(newLang) {
  return getAllTranslations()[newLang];
}

function addVarsToString(string: string, vars: Record<string, string>) {
  let stringWithVars = string;
  Object.keys(vars).forEach((key) => {
    const v = vars[key];
    const reg = new RegExp(`%${key}%`, 'g');

    stringWithVars = stringWithVars.replace(reg, v);
  });

  return stringWithVars;
}

function getCorrectPluralSentence(translation: string, count: number) {
  const translationsSplitted = translation.split(/\|(?![^{]*})/g);
  let returnPhrase = translation;

  if (translation.match(/[{}[\]].*/)) {
    translationsSplitted.some((p) => {
      const matches = p.match(/([{}[\]].*[{}[\]])(.*)/);

      if (matches[1] && amountCheck(matches[1], count)) {
        returnPhrase = matches[2].trim();
        return true;
      }
      returnPhrase = p;

      return false;
    });
  } else {
    return count <= 1 ? translationsSplitted[0].trim() : translationsSplitted[1].trim();
  }

  return returnPhrase;
}

export const getTranslate =
  (getCurrentLang = getActiveLang, getAllMessages = getTranslatedStrings) =>
  (key: string, vars?: Record<string, string>, count?: number) => {
    const currentLang = getCurrentLang();
    const allMessages = getAllMessages(currentLang);

    const translatedText = allMessages?.[key];
    let returnedTranslation = translatedText ?? key;

    const isTranschoice = isNumber(count) && returnedTranslation.match(/\|/);
    if (isTranschoice) {
      returnedTranslation = getCorrectPluralSentence(returnedTranslation, count);
    }

    if (typeof vars !== 'undefined') {
      returnedTranslation = addVarsToString(returnedTranslation, vars);
    }

    if (typeof window !== 'undefined' && window.__TRANSLATION_DEVTOOLS__) {
      window.__TRANSLATION_DEVTOOLS__.addKey(key, translatedText || '');
    }

    return returnedTranslation;
  };

export const translate = getTranslate();

export function reportAllTranslationsToExtension(language: string) {
  if (typeof window !== 'undefined' && window.__TRANSLATION_DEVTOOLS__ && currentScriptSrc) {
    const basePath = currentScriptSrc.substring(0, currentScriptSrc.lastIndexOf('/') + 1);

    const callExtension = (info?: {
      mapping: {
        original: string;
        translations: Record<string, string>;
      }[];
      currentLang: string;
    }) => {
      window?.__TRANSLATION_DEVTOOLS__.addAllTranslations(getTranslatedStrings(language), info);
    };

    fetch(`${basePath}/langs/translation-mapping.json`)
      .then((response) => response.json())
      .then((mapping) => {
        callExtension({
          mapping,
          currentLang: language,
        });
      })
      .catch(() => {
        callExtension();
      });
  }
}

export function setMoment(momentLib) {
  moment = momentLib;
}

export const onReady = () => {
  reportAllTranslationsToExtension(getActiveLang());
};

interface PerRequestCache {
  current: I18nContextType | null;
}

let cache: (() => PerRequestCache) | null = null;

const getCache = () => {
  if (!cache) {
    cache = React.cache((): PerRequestCache => ({ current: null }));
  }
  return cache();
};

export function getI18nServerContext() {
  return getCache().current;
}

export function setI18nServerContext(context: I18nContextType) {
  getCache().current = context;
}

export function createI18nContext(i18n: I18n) {
  const context: I18nContextType = {
    i18n,
    translate: getTranslate(
      () => i18n.lang,
      () => i18n.messages
    ),
  };

  return context;
}
